import React, {Component} from 'react';
import {Navigate} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {LayoutSidebar} from '../../Layout';
import {Spinner} from '../Spinner';
import {KnewsService} from './KnewsService';
import {SupportChatService} from '../support_chat/SupportChatService';
import './Knews.scss';


const CHANGE_TYPE_ICON = {
    bug_fix: '🐛',
    improvement: '🛠',
    new_feature: '✨',
};

const LIFECYCLE_PRODUCTION = 'production';
const LIFECYCLE_BETA_FEEDBACK = 'beta_feedback';
const LIFECYCLE_BETA_REQUEST_ACCESS = 'beta_request_access';

function openIntercomNewMessage(message) {
    // Ruteo por SupportChatService: abre la burbuja de soporte si el user está en la allowlist,
    // o cae a Intercom (mismo guard de siempre) si no. Transparente para el call site.
    try {
        SupportChatService.showNewMessage(message);
        return true;
    } catch (_e) {
        return false;
    }
}


class KnewsPageClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            knews: null,
            loading: true,
            error: null,
            filter: 'all',
            isSuperAdmin: null,  // null = no se sabe todavía; false = redirect
        };
    }

    componentDidMount() {
        this.checkSuperAdmin();
    }

    checkSuperAdmin() {
        new APIClient(API_URL + 'get-logged-user-data/').call('GET')
            .then((data) => {
                const isSuperAdmin = data && data.is_super_admin === true;
                this.setState({isSuperAdmin});
                if (isSuperAdmin) this.fetchKnews();
            })
            .catch(() => this.setState({isSuperAdmin: false}));
    }

    fetchKnews() {
        this.setState({loading: true, error: null});
        KnewsService.list(1, 50)
            .then((data) => {
                const list = (data && data.knews) ? data.knews : [];
                this.setState({knews: list, loading: false});
                // "Entrar a /novedades" = "ver todas". Apaga el puntito rojo
                // del bell aunque el user no clickee item por item.
                KnewsService.markAllSeen().catch(() => {});
            })
            .catch((e) => {
                this.setState({error: e.message || 'Error', loading: false});
            });
    }

    setFilter(filter) {
        this.setState({filter});
    }

    filteredItems() {
        const {knews, filter} = this.state;
        if (!knews) return [];
        if (filter === 'all') return knews;
        return knews.filter((c) => c.change_type === filter);
    }

    formatDate(iso) {
        if (!iso) return '';
        try {
            // "YYYY-MM-DD" (news_date) lo parseamos como fecha local. Si lo
            // pasamos al constructor Date directo, JS lo trata como UTC midnight
            // y en TZ negativa muestra un día menos. ISO datetime (published_at,
            // con T...) sí va directo al constructor.
            let d;
            if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
                const [y, m, day] = iso.split('-').map(Number);
                d = new Date(y, m - 1, day);
            } else {
                d = new Date(iso);
            }
            const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun',
                            'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
            return `${d.getDate()} ${months[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`;
        } catch {
            return iso;
        }
    }

    render() {
        const {t} = this.props;
        const {isSuperAdmin, loading, error, filter} = this.state;

        if (isSuperAdmin === false) {
            return <Navigate to="/" replace/>;
        }

        const items = this.filteredItems();
        const total = this.state.knews ? this.state.knews.length : 0;

        const filters = [
            {key: 'all', label: t('knews.filter_all')},
            {key: 'new_feature', label: `✨ ${t('knews.filter_features')}`},
            {key: 'improvement', label: `🛠 ${t('knews.filter_improvements')}`},
            {key: 'bug_fix', label: `🐛 ${t('knews.filter_bug_fixes')}`},
        ];

        return (
            <div className="app-content">
                <LayoutSidebar/>
                <section className="section-content knews-content">
                    <header className="section-header">
                        <div className="section-info knews-section-info">
                            <h1 className="section-title">{t('knews.title')}</h1>
                            <p className="knews-subtitle">{t('knews.page_subtitle')}</p>
                        </div>
                    </header>

                    <div className="filters">
                        <ul className="knews-filters">
                            {filters.map((f) => (
                                <li key={f.key}>
                                    <button
                                        type="button"
                                        onClick={() => this.setFilter(f.key)}
                                        className={`knews-filter-tab ${filter === f.key ? '-active' : ''}`}
                                    >
                                        {f.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="section-body h-items">
                        <div className="section-body-item -wider knews-list">
                            {loading && (
                                <div className="box-loader"><Spinner color="positive"/></div>
                            )}
                            {!loading && error && (
                                <div className="knews-empty -error">{t('knews.load_error')}</div>
                            )}
                            {!loading && !error && total === 0 && (
                                <div className="knews-empty">{t('knews.empty_long')}</div>
                            )}
                            {!loading && !error && items.map((c, idx) => (
                                <KnewCard
                                    key={c.id}
                                    knew={c}
                                    index={idx}
                                    formatDate={this.formatDate}
                                    t={t}
                                />
                            ))}
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}

function KnewCard({knew, index, formatDate, t}) {
    const wasUnread = !knew.seen;
    const lifecycle = knew.lifecycle_status || LIFECYCLE_PRODUCTION;
    const isBetaRequest = lifecycle === LIFECYCLE_BETA_REQUEST_ACCESS;
    const isBetaFeedback = lifecycle === LIFECYCLE_BETA_FEEDBACK;

    const trackAndOpen = (url, external) => (event) => {
        event.preventDefault();
        KnewsService.trackClick(knew.id).catch(() => {});
        if (external) {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            // In-app navigation
            window.location.href = url;
        }
    };

    const handleRequestAccess = (event) => {
        event.preventDefault();
        KnewsService.trackClick(knew.id).catch(() => {});
        const prefix = t('knews.beta_request_access_intercom_prefix');
        openIntercomNewMessage(`${prefix} ${knew.title}`);
    };

    const ctaText = knew.cta_text;
    const linkUrl = knew.link_url;
    const helpUrl = knew.help_article_url;
    const mediaUrl = knew.media_url;

    const classNames = [
        'knews-card',
        wasUnread ? '-fresh' : '',
        isBetaRequest ? '-beta-request' : '',
        isBetaFeedback ? '-beta-feedback' : '',
    ].filter(Boolean).join(' ');

    return (
        <article className={classNames} style={{'--entry-delay': `${index * 50}ms`}}>
            {mediaUrl && (
                <div className="knews-card__media">
                    <img src={mediaUrl} alt=""/>
                </div>
            )}
            <header className="knews-card__head">
                <span className="knews-card__date">
                    {formatDate(knew.news_date || knew.published_at)}
                </span>
                <span className={`knews-card__type -${knew.change_type}`}>
                    <span aria-hidden="true">{CHANGE_TYPE_ICON[knew.change_type] || '·'}</span>
                    <span>{t(`knews.type_${knew.change_type}`)}</span>
                </span>
                {isBetaRequest && (
                    <span className="knews-card__lifecycle -request">
                        {t('knews.lifecycle_beta_request_access')}
                    </span>
                )}
                {isBetaFeedback && (
                    <span className="knews-card__lifecycle -feedback">
                        {t('knews.lifecycle_beta_feedback')}
                    </span>
                )}
                {wasUnread && <span className="knews-card__fresh">{t('knews.fresh')}</span>}
            </header>
            <h2 className="knews-card__title">{knew.title}</h2>
            <div className="knews-card__body">{knew.body}</div>
            {isBetaFeedback && (
                <p className="knews-card__beta-note">
                    {t('knews.beta_feedback_note')}
                </p>
            )}
            {(ctaText || linkUrl || helpUrl || isBetaRequest) && (
                <div className="knews-card__actions">
                    {isBetaRequest && (
                        <button type="button"
                                onClick={handleRequestAccess}
                                className="btn -alt">
                            {t('knews.beta_request_access_cta')} <span aria-hidden="true">→</span>
                        </button>
                    )}
                    {!isBetaRequest && ctaText && linkUrl && (
                        <a href={linkUrl}
                           onClick={trackAndOpen(linkUrl, /^https?:/i.test(linkUrl))}
                           className="btn -alt">
                            {ctaText} <span aria-hidden="true">→</span>
                        </a>
                    )}
                    {!isBetaRequest && !ctaText && linkUrl && (
                        <a href={linkUrl}
                           onClick={trackAndOpen(linkUrl, /^https?:/i.test(linkUrl))}
                           className="knews-card__link">
                            {t('knews.go_to_feature')} <span aria-hidden="true">→</span>
                        </a>
                    )}
                    {helpUrl && (
                        <a href={helpUrl}
                           onClick={trackAndOpen(helpUrl, true)}
                           className="knews-card__help">
                            {t('knews.see_in_help')} <span aria-hidden="true">↗</span>
                        </a>
                    )}
                </div>
            )}
        </article>
    );
}

export const KnewsPage = withTranslation()(KnewsPageClass);
export default KnewsPage;
